import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  type ChangeEvent,
} from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import NumberInput from '@/components/inputControls/NumberInput';

import { Container } from '@/components/container';
import { DataGrid, KeenIcon } from '@/components';
import {
  Toolbar,
  ToolbarHeading,
  ToolbarPageTitle,
  ToolbarDescription,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import usePagination from '@/hooks/usePagination';
import { ConfirmationDialog } from '@/components/dialogBox';
import { selectUserPermissions } from '@/store/authSlice';

import {
  useGetAccountingExportRunsQuery,
  useRunAccountingExportMutation,
  useGetAccountingExportCronConfigQuery,
  useUpdateAccountingExportCronConfigMutation,
} from '@/services/AccountingExportsService';
import {
  AccountingExportRunItem,
  AccountingExportRunsListParams,
} from '@/types/accounting-exports.types';
import { AccountingExportRunStatus } from '@/constant';
import { formatDisplayDate } from '@/utils/Date';
import { formatDisplayDateTime } from '@/utils/Date';

const RUN_STATUS_LABELS: Record<string, string> = {
  [AccountingExportRunStatus.PENDING]: 'Pending',
  [AccountingExportRunStatus.RUNNING]: 'Running',
  [AccountingExportRunStatus.SUCCEEDED]: 'Succeeded',
  [AccountingExportRunStatus.PARTIALLY_SUCCEEDED]: 'Partially Succeeded',
  [AccountingExportRunStatus.FAILED]: 'Failed',
};

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case AccountingExportRunStatus.SUCCEEDED:
      return 'badge-success';
    case AccountingExportRunStatus.PARTIALLY_SUCCEEDED:
      return 'badge-warning';
    case AccountingExportRunStatus.RUNNING:
      return 'badge-warning';
    case AccountingExportRunStatus.PENDING:
      return 'badge-secondary';
    case AccountingExportRunStatus.FAILED:
      return 'badge-danger';
    default:
      return 'badge-secondary';
  }
};

const AccountingExports = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const userPermissions = useSelector(selectUserPermissions);

  const hasRunPermission = useMemo(() => {
    return userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('netvisor_accounting_exports_run')
    );
  }, [userPermissions]);

  const [dateRange, setDateRange] = useState<
    [string | undefined, string | undefined]
  >([undefined, undefined]);
  const [runsFilterRange, setRunsFilterRange] = useState<
    [string | undefined, string | undefined]
  >([undefined, undefined]);
  const [executeNow, setExecuteNow] = useState<boolean>(false);
  const [cronMinutes, setCronMinutes] = useState<number>(0);
  const [isRunConfirmOpen, setIsRunConfirmOpen] = useState(false);
  const runsListRef = useRef<HTMLDivElement | null>(null);
  const triggerExportSectionRef = useRef<HTMLElement | null>(null);

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    pagination,
    setPagination,
  } = usePagination();

  const listParams: AccountingExportRunsListParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    period_start_from: runsFilterRange[0]
      ? moment
        .utc(runsFilterRange[0], 'DD/MM/YYYY')
        .startOf('day')
        .toISOString()
      : undefined,
    period_end_to: runsFilterRange[1]
      ? moment.utc(runsFilterRange[1], 'DD/MM/YYYY').endOf('day').toISOString()
      : undefined,
  };

  const {
    data: list,
    isFetching: listLoading,
    error: listError,
  } = useGetAccountingExportRunsQuery(listParams);

  const [runExport, { isLoading: isRunning }] =
    useRunAccountingExportMutation();

  const { data: configData, isFetching: configLoading } =
    useGetAccountingExportCronConfigQuery();

  const [updateConfig, { isLoading: isUpdatingConfig }] =
    useUpdateAccountingExportCronConfigMutation();

  useEffect(() => {
    if (configData?.data?.minutes !== undefined) {
      setCronMinutes(configData.data.minutes);
    }
  }, [configData]);

  const rows: AccountingExportRunItem[] = useMemo(
    () => list?.items ?? [],
    [list]
  );

  useEffect(() => {
    if (list) {
      handleTotalPages(list.totalPages || 1);
      handleTotalItems(list.totalItems || 0);
    }
  }, [list, handleTotalItems, handleTotalPages]);

  useEffect(() => {
    setPagination((prev) => {
      if (prev.pageIndex === 0) return prev;
      return {
        ...prev,
        pageIndex: 0,
      };
    });
  }, [runsFilterRange, setPagination]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (document.activeElement && document.activeElement !== document.body) {
        return;
      }
      runsListRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const state = {
      enabled: true,
      redirected: false,
    };

    const disable = () => {
      state.enabled = false;
    };

    const onUserInteraction = () => {
      disable();
    };

    const onFocusIn = (event: FocusEvent) => {
      if (!state.enabled || state.redirected) return;

      const target = event.target as HTMLElement | null;
      if (!target) return;

      const withinTriggerSection =
        !!triggerExportSectionRef.current &&
        triggerExportSectionRef.current.contains(target);

      if (!withinTriggerSection) return;

      const isRmdpInput =
        target.classList.contains('rmdp-input') ||
        !!target.closest('.rmdp-container') ||
        !!target.closest('.rmdp-wrapper');

      if (!isRmdpInput) return;

      state.redirected = true;
      event.preventDefault();
      window.setTimeout(() => {
        runsListRef.current?.focus();
      }, 0);
    };

    document.addEventListener('focusin', onFocusIn, true);
    window.addEventListener('pointerdown', onUserInteraction, true);
    window.addEventListener('keydown', onUserInteraction, true);

    const disableTimer = window.setTimeout(() => {
      disable();
    }, 1000);

    return () => {
      window.clearTimeout(disableTimer);
      document.removeEventListener('focusin', onFocusIn, true);
      window.removeEventListener('pointerdown', onUserInteraction, true);
      window.removeEventListener('keydown', onUserInteraction, true);
    };
  }, []);

  useEffect(() => {
    if (!listError) return;

    const err = listError as unknown as {
      data?: unknown;
      error?: string;
    } | null;

    let message = 'Failed to fetch accounting export runs';
    if (
      err?.data &&
      typeof err.data === 'object' &&
      'message' in (err.data as Record<string, unknown>)
    ) {
      const m = (err.data as { message?: string }).message;
      if (typeof m === 'string' && m) message = m;
    } else if (typeof err?.error === 'string' && err.error) {
      message = err.error;
    }

    enqueueSnackbar(message, { variant: 'error' });
  }, [listError, enqueueSnackbar]);

  const handleOpenRunConfirm = useCallback(() => {
    if (!hasRunPermission) {
      enqueueSnackbar('You do not have permission to run exports', {
        variant: 'error',
      });
      return;
    }

    if (!dateRange[0] || !dateRange[1]) {
      enqueueSnackbar('Please select a start and end date', {
        variant: 'warning',
      });
      return;
    }

    setIsRunConfirmOpen(true);
  }, [dateRange, enqueueSnackbar, hasRunPermission]);

  const handleRunExport = useCallback(async () => {
    if (!dateRange[0] || !dateRange[1]) return;

    try {
      const start = moment.utc(dateRange[0], 'DD/MM/YYYY').startOf('day');
      const end = moment.utc(dateRange[1], 'DD/MM/YYYY').endOf('day');

      await runExport({
        period_start: start.toISOString(),
        period_end: end.toISOString(),
        execute: executeNow,
      }).unwrap();

      enqueueSnackbar('Accounting export run triggered', {
        variant: 'success',
      });
      setIsRunConfirmOpen(false);
    } catch (e) {
      const err = e as unknown as { data?: unknown; error?: string };
      let message = 'Failed to trigger accounting export run';
      if (
        err?.data &&
        typeof err.data === 'object' &&
        'message' in (err.data as Record<string, unknown>)
      ) {
        const m = (err.data as { message?: string }).message;
        if (typeof m === 'string' && m) message = m;
      } else if (typeof err?.error === 'string' && err.error) {
        message = err.error;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [dateRange, enqueueSnackbar, executeNow, runExport]);

  const handleUpdateConfig = useCallback(async () => {
    try {
      await updateConfig({ minutes: cronMinutes }).unwrap();
      enqueueSnackbar('Accounting export scheduler configuration updated', {
        variant: 'success',
      });
    } catch (e) {
      const err = e as unknown as { data?: unknown; error?: string };
      let message =
        'Failed to update accounting export scheduler configuration';
      if (
        err?.data &&
        typeof err.data === 'object' &&
        'message' in (err.data as Record<string, unknown>)
      ) {
        const m = (err.data as { message?: string }).message;
        if (typeof m === 'string' && m) message = m;
      } else if (typeof err?.error === 'string' && err.error) {
        message = err.error;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [cronMinutes, enqueueSnackbar, updateConfig]);

  const columns = useMemo(
    () => [
      {
        accessorFn: (row: AccountingExportRunItem) => row.period_start,
        id: 'period',
        header: 'Period',
        enableSorting: false,
        cell: ({ row }: { row: { original: AccountingExportRunItem } }) => {
          const start = row.original.period_start
            ? formatDisplayDate(row.original.period_start, '')
            : '';
          const end = row.original.period_end
            ? formatDisplayDate(row.original.period_end, '')
            : '';
          return (
            <span>
              {start} - {end}
            </span>
          );
        },
      },
      {
        accessorFn: (row: AccountingExportRunItem) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: AccountingExportRunItem } }) => (
          <span className={`badge ${getStatusBadgeClass(row.original.status)}`}>
            {RUN_STATUS_LABELS[row.original.status] || row.original.status}
          </span>
        ),
      },
      {
        accessorFn: (row: AccountingExportRunItem) => row.entry_count,
        id: 'entry_count',
        header: 'Entries',
        enableSorting: false,
      },
      {
        accessorFn: (row: AccountingExportRunItem) => row.exported_entry_count,
        id: 'exported_entry_count',
        header: 'Exported',
        enableSorting: false,
      },
      {
        accessorFn: (row: AccountingExportRunItem) => row.failed_entry_count,
        id: 'failed_entry_count',
        header: 'Failed',
        enableSorting: false,
      },
      {
        accessorFn: (row: AccountingExportRunItem) => row.created_at,
        id: 'created_at',
        header: 'Created',
        enableSorting: false,
        cell: ({ row }: { row: { original: AccountingExportRunItem } }) => (
          <span className="text-sm">
            {row.original.created_at
              ? formatDisplayDateTime(row.original.created_at)
              : ''}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: AccountingExportRunItem } }) => (
          <Link
            to={`/netvisor/accounting-exports/${row.original.id}`}
            className="btn btn-sm btn-ghost gap-2"
            aria-label={`View accounting export run ${row.original.id}`}
          >
            <KeenIcon icon="arrow-right" className="text-lg" />
            View
          </Link>
        ),
      },
    ],
    []
  );

  return (
    <>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle
                id="page-title"
                text="Netvisor – Accounting Exports"
              />
              <ToolbarDescription>
                Manage Netvisor accounting export runs and review entries.
              </ToolbarDescription>
            </ToolbarHeading>
          </Toolbar>
        </Container>
      )}

      <Container>
        <div aria-labelledby="page-title" className="py-6 text-gray-900">
          <div className="flex gap-3 flex-col sm:flex-row">
            <section
              ref={triggerExportSectionRef}
              aria-label="Run accounting export"
              className="mb-6 flex-1"
            >
              <div className="rounded-lg flex flex-col h-full border border-gray-200 bg-gray-100 p-4 contrast:bg-black">
                <h2 className="text-base font-semibold text-gray-900">
                  Trigger export run
                </h2>

                <div className="mt-4 flex-1 grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="md:col-span-2">
                    <label
                      id="period-label"
                      htmlFor="period_start"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Period
                    </label>
                    <div className="mt-1" aria-labelledby="period-label">
                      <DateRangePicker
                        startDateName="period_start"
                        endDateName="period_end"
                        isMandatory={false}
                        allowPastDates
                        value={dateRange}
                        onChange={(range) => setDateRange(range)}
                      />
                    </div>
                  </div>

                  <div>
                    <fieldset className="mt-6">
                      <legend className="sr-only">Execution</legend>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={executeNow}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setExecuteNow(e.target.checked)
                          }
                          className="h-4 w-4 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700 cursor-pointer">
                          Execute immediately
                        </span>
                      </label>
                    </fieldset>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleOpenRunConfirm}
                    disabled={!hasRunPermission}
                    className="btn btn-primary"
                    aria-disabled={!hasRunPermission}
                  >
                    {isRunning ? 'Running…' : 'Run Export'}
                  </button>

                  {!hasRunPermission && (
                    <p className="text-sm text-gray-600">
                      You don’t have permission to trigger runs.
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section
              aria-label="Scheduler configuration"
              className="mb-6 flex-1"
            >
              <div className="rounded-lg h-full border border-gray-200 bg-gray-100 p-4 contrast:bg-black">
                <h2 className="text-base font-semibold text-gray-900">
                  Scheduler configuration
                </h2>

                <div className="mt-4">
                  <div className="">
                    <label
                      id="cron-minutes-label"
                      htmlFor="cron_minutes"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Frequency (Minutes)
                    </label>
                    <div className="mt-1">
                      <NumberInput
                        id="cron_minutes"
                        name="cron_minutes"
                        min="1"
                        value={cronMinutes}
                        disabled={configLoading}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setCronMinutes(parseInt(e.target.value) || 0)
                        }
                        className="input input-sm w-full"
                        aria-labelledby="cron-minutes-label"
                        autoComplete="off"
                      />
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Defines how often the backend should run the exports.
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleUpdateConfig}
                    disabled={isUpdatingConfig || configLoading}
                    className="btn btn-primary"
                  >
                    {isUpdatingConfig ? 'Updating…' : 'Update Configuration'}
                  </button>
                </div>
              </div>
            </section>
          </div>

          <section aria-label="Accounting export runs">
            <h2 className="sr-only">Accounting export runs list</h2>

            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-100 p-4 contrast:bg-black">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="w-full md:max-w-md">
                  <label
                    id="filter-period-label"
                    htmlFor="period_start_from"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Filter by period
                  </label>
                  <div className="mt-1" aria-labelledby="filter-period-label">
                    <DateRangePicker
                      startDateName="period_start_from"
                      endDateName="period_end_to"
                      isMandatory={false}
                      value={runsFilterRange}
                      onChange={(range) => setRunsFilterRange(range)}
                      allowPastDates={true}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => setRunsFilterRange([undefined, undefined])}
                    disabled={!runsFilterRange[0] && !runsFilterRange[1]}
                    aria-disabled={!runsFilterRange[0] && !runsFilterRange[1]}
                  >
                    Clear filter
                  </button>
                </div>
              </div>
            </div>

            <div
              ref={runsListRef}
              tabIndex={-1}
              aria-label="Accounting export runs list"
              className="rounded-lg border border-gray-200 bg-gray-100 contrast:bg-black"
            >
              <DataGrid
                columns={columns}
                data={rows}
                loading={listLoading}
                serverSide={true}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  totalPages,
                  totalItems,
                  setPagination,
                }}
              />
            </div>
          </section>
        </div>
      </Container>

      <ConfirmationDialog
        open={isRunConfirmOpen}
        onClose={() => setIsRunConfirmOpen(false)}
        onConfirm={handleRunExport}
        confirmButtonText={executeNow ? 'Run & execute' : 'Run'}
        message={
          <div>
            <p className="text-sm text-gray-700">
              This will create a Netvisor accounting export for the selected
              period.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              Execute immediately: <strong>{executeNow ? 'Yes' : 'No'}</strong>
            </p>
          </div>
        }
        disabled={isRunning}
        isLoading={isRunning}
      />
    </>
  );
};

export default AccountingExports;
